import React, { useEffect, useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { SupplierRead, SortDirection, FormStatus } from '@/types';
import { logger } from '@/utils/secureLogger';
import { trackCreate, trackDelete, trackEdit, trackRestore } from '@/services/analytics';
import { getSupplierDoc, getSupplierDocs, addSupplierDoc, setSupplierDoc, deleteSupplierDoc, restoreSupplierDoc } from '@/services/firebaseApi';

export enum SuppliersSortBy {
    ID = 'supplierId',
    NAME = 'name',
    EMAIL = 'email',
    PHONE = 'phone',
}

interface UseSuppliersProps {
    storageId?: string;
    supplierId?: string;
}

const useSuppliers = ({ storageId, supplierId }: UseSuppliersProps) => {
    const queryClient = useQueryClient();
    
    // Filter states
    const [sortBy, setSortBy] = useState(SuppliersSortBy.NAME);
    const [sortDirection, setSortDirection] = useState(SortDirection.ASCENDING);
    const [filterQuery, setFilterQuery] = useState<string>('');
    const [displayDeleted, setDisplayDeleted] = useState<boolean>(false);

    // All suppliers query
    const {
        data: allSuppliers,
        isLoading: suppliersLoading,
        error: suppliersError,
        refetch: refetchSuppliers
    } = useQuery({
        queryKey: ['suppliers', storageId, displayDeleted],
        queryFn: () => getSupplierDocs(storageId!, displayDeleted),
        enabled: !!(storageId && !supplierId),
        staleTime: 5 * 60 * 1000, // Supplier data doesn't change often
        gcTime: 15 * 60 * 1000,
    });

    // Single supplier query
    const {
        data: supplier,
        isLoading: supplierLoading,
        error: supplierError,
        refetch: refetchSupplier
    } = useQuery({
        queryKey: ['supplier', storageId, supplierId],
        queryFn: () => getSupplierDoc(storageId!, supplierId!),
        enabled: !!(storageId && supplierId),
        staleTime: 5 * 60 * 1000,
    });

    // Apply client-side filtering and sorting
    const filteredSuppliers = useMemo(() => {
        if (!allSuppliers) return [];
        
        let filtered = [...allSuppliers];

        // Apply text filter
        if (filterQuery) {
            const lower = filterQuery.toLowerCase();
            filtered = filtered.filter((supplier) =>
                supplier.supplierId.toString().includes(lower) ||
                supplier.name.toLowerCase().includes(lower) ||
                (supplier.contactInformation?.email && supplier.contactInformation?.email.toLowerCase().includes(lower)) ||
                (supplier.contactInformation?.phone && supplier.contactInformation?.phone.toLowerCase().includes(lower)) ||
                (supplier.notes && supplier.notes.toLowerCase().includes(lower))
            );
        }

        // Note: displayDeleted filtering is now handled server-side via the includeDeleted parameter

        return filtered;
    }, [allSuppliers, filterQuery]);

    // Mutations
    const createSupplierMutation = useMutation({
        mutationFn: addSupplierDoc,
        onSuccess: (_, variables) => {
            trackCreate('supplier');
            logger.info('Supplier created successfully');
            queryClient.invalidateQueries({ queryKey: ['suppliers', variables.ids.storageId, true] });
            queryClient.invalidateQueries({ queryKey: ['suppliers', variables.ids.storageId, false] });
            queryClient.invalidateQueries({ queryKey: ['storage', variables.ids.storageId] });
            queryClient.invalidateQueries({ queryKey: ['storages'] });
        },
        onError: (error) => {
            logger.error('Failed to create supplier:', error, 'useSuppliers');
        }
    });

    const updateSupplierMutation = useMutation({
        mutationFn: setSupplierDoc,
        onSuccess: (_, variables) => {
            trackEdit('supplier');
            logger.info('Supplier updated successfully:', variables.ids.supplierId);
            queryClient.invalidateQueries({ queryKey: ['suppliers', variables.ids.storageId, true] });
            queryClient.invalidateQueries({ queryKey: ['suppliers', variables.ids.storageId, false] });
            queryClient.invalidateQueries({ queryKey: ['supplier', variables.ids.storageId, variables.ids.supplierId] });
        },
        onError: (error) => {
            logger.error('Failed to update supplier:', error, 'useSuppliers');
        }
    });

    const deleteSupplierMutation = useMutation({
        mutationFn: deleteSupplierDoc,
        onSuccess: (_, variables) => {
            trackDelete('supplier');
            logger.info('Supplier deleted successfully:', variables.supplierId);
            queryClient.invalidateQueries({ queryKey: ['suppliers', variables.storageId, true] });
            queryClient.invalidateQueries({ queryKey: ['suppliers', variables.storageId, false] });
            queryClient.invalidateQueries({ queryKey: ['supplier', variables.storageId, variables.supplierId] });
        },
        onError: (error) => {
            logger.error('Failed to delete supplier:', error, 'useSuppliers');
        }
    });

    const restoreSupplierMutation = useMutation({
        mutationFn: restoreSupplierDoc,
        onSuccess: (_, variables) => {
            trackRestore('supplier');
            logger.info('Supplier restored successfully:', variables.supplierId);
            queryClient.invalidateQueries({ queryKey: ['suppliers', variables.storageId, true] });
            queryClient.invalidateQueries({ queryKey: ['suppliers', variables.storageId, false] });
            queryClient.invalidateQueries({ queryKey: ['supplier', variables.storageId, variables.supplierId] });
        },
        onError: (error) => {
            logger.error('Failed to restore supplier:', error, 'useSuppliers');
        }
    });

    // Helper function to determine status
    const getStatus = (loading: boolean, error: any): FormStatus => {
        if (loading) return FormStatus.LOADING;
        if (error) return FormStatus.ERROR;
        return FormStatus.SUCCESS;
    };

    // Backward compatible functions
    const reloadSuppliers = () => {
        if (supplierId) {
            refetchSupplier();
        } else {
            refetchSuppliers();
        }
    };

    return {
        // Single supplier
        supplier: supplier || null,
        supplierStatus: getStatus(supplierLoading, supplierError),
        
        // Multiple suppliers
        suppliers: allSuppliers || [],
        filteredSuppliers,
        suppliersStatus: getStatus(suppliersLoading, suppliersError),
        
        // Filtering and sorting
        sortBy,
        setSortBy,
        sortDirection,
        setSortDirection,
        filterQuery,
        setFilterQuery,
        displayDeleted,
        setDisplayDeleted,
        
        // Actions
        reloadSuppliers,
        
        // Mutations (async only)
        createSupplierAsync: createSupplierMutation.mutateAsync,
        updateSupplierAsync: updateSupplierMutation.mutateAsync,
        deleteSupplierAsync: deleteSupplierMutation.mutateAsync,
        restoreSupplierAsync: restoreSupplierMutation.mutateAsync,
        isCreating: createSupplierMutation.isPending,
        isUpdating: updateSupplierMutation.isPending,
        isDeleting: deleteSupplierMutation.isPending,
        isRestoring: restoreSupplierMutation.isPending,
        
        // Refresh functions
        refreshSuppliers: refetchSuppliers,
        refreshSupplier: refetchSupplier,
    };
};

export default useSuppliers;
